// Every mechanic, with its card from src/content/cards.ts resolved onto it.
// Everything outside the spells reads a Spell: name, blurb, prompt, and
// whether those are the voice or still the spec.

import { CARDS } from '../content/cards';
import { BACKFIRES } from './mechanics/backfires';
import { BOARDWIDE } from './mechanics/boardwide';
import { BUFFS } from './mechanics/buffs';
import { DEBUFFS } from './mechanics/debuffs';
import type { Spell, SpellFamily, SpellId, SpellMechanic, SpellPolarity } from './types';

/** "grant-knight" reads as "grant knight" on a surface with no card yet. */
const specName = (id: SpellId): string => id.replace(/-/g, ' ');

/** A field of a card that is not written yet is missing or blank. */
const isWritten = (text: string | undefined): text is string => text !== undefined && text !== '';

const resolve = (mechanic: SpellMechanic): Spell => {
  const card = CARDS[mechanic.id];
  const name = isWritten(card?.name) ? card.name : specName(mechanic.id);
  const blurb = isWritten(card?.blurb) ? card.blurb : mechanic.spec.summary;
  const prompt = isWritten(card?.prompt) ? card.prompt : mechanic.spec.prompt;
  const needsPrompt = mechanic.targetCount > 0;
  const voiced =
    isWritten(card?.name) && isWritten(card?.blurb) && (!needsPrompt || isWritten(card?.prompt));
  return { ...mechanic, name, blurb, prompt, voiced };
};

/** Every spell. The files of the mechanics keep their first names, which are no longer the polarities. */
export const ALL_SPELLS: readonly Spell[] = [...BUFFS, ...DEBUFFS, ...BACKFIRES, ...BOARDWIDE].map(
  resolve,
);

const withPolarity = (polarity: SpellPolarity): Spell[] =>
  ALL_SPELLS.filter((spell) => spell.polarity === polarity);

export const SPELLS_BY_POLARITY: Record<SpellPolarity, readonly Spell[]> = {
  ally: withPolarity('ally'),
  enemy: withPolarity('enemy'),
  any: withPolarity('any'),
  board: withPolarity('board'),
};

const SPELLS_BY_ID = new Map(ALL_SPELLS.map((spell) => [spell.id, spell]));

export const spellById = (id: SpellId): Spell => {
  const spell = SPELLS_BY_ID.get(id);
  if (!spell) throw new Error(`Unknown spell "${id}"`);
  return spell;
};

export const POLARITY_LABELS: Record<SpellPolarity, string> = {
  ally: 'Ally',
  enemy: 'Enemy',
  any: 'Any',
  board: 'Board',
};

export const FAMILY_LABELS: Record<SpellFamily, string> = {
  ability: 'Ability',
  status: 'Status',
  placement: 'Placement',
  ground: 'Ground',
  royalty: 'Royalty',
  timer: 'Timer',
};

/**
 * Classes a surface adds to wear a spell's color and pattern, and the spec
 * class while the spell has no card.
 */
export const spellClasses = (prefix: string, spell: Spell): string =>
  `${prefix}--${spell.polarity} ${prefix}--${spell.family}${spell.voiced ? '' : ` ${prefix}--spec`}`;
